import { computed } from 'vue';
import { useAppStore } from '@/stores/AppStore';

/**
 * Composable for checking user permissions and roles
 */
export function usePermissions() {
    const appStore = useAppStore();

    /**
     * Get the current user's roles as an array of role names
     */
    const userRoles = computed((): string[] => {
        const rolesRecord = appStore.account.user?.Roles;
        if (!rolesRecord) return [];
        return Object.values(rolesRecord)
            .map(role => role?.Name || '')
            .filter(name => name.length > 0);
    });

    /**
     * Get the current user's claims as an array
     */
    const userClaims = computed(() => {
        const claimsRecord = appStore.account.user?.Claims;
        if (!claimsRecord) return [];
        return Object.values(claimsRecord);
    });

    /**
     * Check if user has a specific role (case-insensitive)
     */
    const hasRole = (roleName: string): boolean => {
        return userRoles.value.some(r => r.toLowerCase() === roleName.toLowerCase());
    };

    /**
     * Check if user has any of the specified roles
     */
    const hasAnyRole = (roleNames: string[]): boolean => {
        return roleNames.some(name => hasRole(name));
    };

    /**
     * Check if user has all of the specified roles
     */
    const hasAllRoles = (roleNames: string[]): boolean => {
        return roleNames.every(name => hasRole(name));
    };

    /**
     * Check if user has a specific claim
     */
    const hasClaim = (claimType: string, claimValue?: string): boolean => {
        return userClaims.value.some(claim => {
            if (claim?.ClaimType?.toLowerCase() !== claimType.toLowerCase()) return false;
            if (claimValue !== undefined) {
                return claim?.ClaimValue?.toLowerCase() === claimValue.toLowerCase();
            }
            return true;
        });
    };

    /**
     * Whether the current user is an admin (has admin claim with sysadmin value)
     * This matches the backend check in RequestController.ApplyRoleFilter
     * Also checks for Admin role for backward compatibility
     */
    const isAdmin = computed(() => hasClaim('admin', 'sysadmin') || hasRole('Admin'));

    /**
     * Whether the current user is authenticated
     */
    const isAuthenticated = computed(() => {
        return !!appStore.account.bearer && !!appStore.account.user;
    });

    /**
     * Current user info
     */
    const currentUser = computed(() => appStore.account.user);

    /**
     * Current user ID
     */
    const userId = computed(() => appStore.account.user?.UserId);

    /**
     * Current staff ID (GUID)
     */
    const staffId = computed(() => appStore.account.user?.StaffId);

    /**
     * Whether the current user is a staff member (not an admin user)
     * Staff users have StaffId and no UserId (or UserId === 0)
     */
    const isStaff = computed(() => {
        const user = appStore.account.user;
        return !!user?.StaffId &&
               user.StaffId !== '00000000-0000-0000-0000-000000000000' &&
               !user.UserId;
    });

    return {
        userRoles,
        userClaims,
        hasRole,
        hasAnyRole,
        hasAllRoles,
        hasClaim,
        isAdmin,
        isStaff,
        isAuthenticated,
        currentUser,
        userId,
        staffId
    };
}

export default usePermissions;
